import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Activity, Calendar, Wallet, Suitcase, UserCrown, CheckCircle, UserPlus } from 'iconoir-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Relative-time formatter — "2h ago", "Mar 14", etc. */
function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso);
  const diffMs = Date.now() - then.getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 7) return `${days}d ago`;
  return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const ENTITY_ICONS = {
  event: Calendar,
  expense: Wallet,
  vendor: Suitcase,
  guest: UserCrown,
  task: CheckCircle,
  collaborator: UserPlus,
};

const ENTITY_NOUN = {
  event: 'event',
  expense: 'expense',
  vendor: 'vendor',
  guest: 'guest',
  task: 'task',
  collaborator: 'collaborator',
};

const ACTION_VERB = {
  created: 'added',
  updated: 'updated',
  deleted: 'removed',
  completed: 'completed',
  reopened: 'reopened',
  invited: 'invited',
  removed: 'removed',
};

function initials(name) {
  if (!name) return '?';
  return name
    .split(/\s+/)
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/** Deterministic soft-pastel accent from the actor's name. */
function accentForName(name) {
  const palette = [
    { bg: 'bg-terracotta/10', text: 'text-terracotta' },
    { bg: 'bg-moss/10', text: 'text-moss' },
    { bg: 'bg-ochre/10', text: 'text-ochre' },
    { bg: 'bg-charcoal/10', text: 'text-charcoal' },
  ];
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return palette[hash % palette.length];
}

export default function ActivityFeedCard({ weddingId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    const run = async () => {
      if (!weddingId) return;
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/activity/${weddingId}?limit=15`, {
          withCredentials: true,
        });
        if (!cancel) setItems(data?.items || []);
      } catch (err) {
        if (!cancel) setItems([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    run();
    return () => { cancel = true; };
  }, [weddingId]);

  if (loading) return null;
  if (!items.length) return null; // Self-hide until there's actually something to show

  return (
    <Card className="card-surface p-6" data-testid="activity-feed-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-muted" strokeWidth={1.5} />
          <h3 className="font-serif text-xl text-charcoal">Recent activity</h3>
        </div>
        <span className="text-[11px] uppercase tracking-widest text-muted">
          Last {items.length}
        </span>
      </div>
      <ul className="space-y-3" data-testid="activity-feed-list">
        {items.map((it) => {
          const Icon = ENTITY_ICONS[it.entity] || Activity;
          const verb = ACTION_VERB[it.action] || it.action;
          const noun = ENTITY_NOUN[it.entity] || it.entity;
          const accent = accentForName(it.actor_name);
          return (
            <li
              key={it.id}
              className="flex items-start gap-3"
              data-testid={`activity-item-${it.id}`}
            >
              <div
                className={`shrink-0 w-8 h-8 rounded-full ${accent.bg} ${accent.text} flex items-center justify-center text-[11px] font-mono font-medium`}
                aria-hidden="true"
              >
                {initials(it.actor_name)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-charcoal leading-snug">
                  <span className="font-medium">{it.actor_name}</span>{' '}
                  <span className="text-muted">{verb}</span>{' '}
                  <span className="text-muted">{noun}</span>
                  {it.entity_name && (
                    <>
                      {' '}
                      <span className="text-charcoal truncate">{it.entity_name}</span>
                    </>
                  )}
                </p>
                <p className="text-[11px] text-muted mt-0.5 flex items-center gap-1.5">
                  <Icon className="w-3 h-3" strokeWidth={1.5} />
                  {relativeTime(it.created_at)}
                </p>
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
